import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { 
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Building2,
  MapPin,
  FileText,
  DollarSign,
  Upload,
  CheckCircle2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 'basic', label: 'Información Básica', icon: Building2 },
  { id: 'location', label: 'Ubicación', icon: MapPin },
  { id: 'details', label: 'Detalles', icon: FileText },
  { id: 'financial', label: 'Financiero', icon: DollarSign },
  { id: 'documents', label: 'Documentos', icon: Upload },
  { id: 'review', label: 'Revisión', icon: CheckCircle2 },
];

const PropertyFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useI18n();
  
  const isEditing = !!id;
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    property_type: '',
    // Location
    address: '',
    city: '',
    state: '',
    country: 'México',
    postal_code: '',
    // Details
    land_area_m2: '',
    built_area_m2: '',
    floors: '',
    year_built: '',
    zoning: '',
    land_use: '',
    occupancy_status: '',
    current_rent_monthly: '',
    // Legal
    legal_status: '',
    ownership_structure: '',
    owner_type: 'individual',
    existing_liens: '',
    encumbrances: '',
    // Financial
    asking_value: '',
    asking_value_currency: 'MXN',
    desired_structure: '',
    contribution_reason: '',
    urgency: '',
    notes: '',
    // Checkboxes
    has_title_deed: false,
    has_tax_clearance: false,
    has_no_liens_cert: false,
    has_utility_bills: false,
  });

  useEffect(() => {
    if (isEditing) {
      fetchProperty();
    }
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await api.get(`/properties/${id}`);
      const data = response.data;
      setProperty(data);
      setFormData({
        title: data.title || '',
        property_type: data.property_type || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        country: data.country || 'México',
        postal_code: data.postal_code || '',
        land_area_m2: data.land_area_m2 || '',
        built_area_m2: data.built_area_m2 || '',
        floors: data.floors || '',
        year_built: data.year_built || '',
        zoning: data.zoning || '',
        land_use: data.land_use || '',
        occupancy_status: data.occupancy_status || '',
        current_rent_monthly: data.current_rent_monthly || '',
        legal_status: data.legal_status || '',
        ownership_structure: data.ownership_structure || '',
        owner_type: data.owner_type || 'individual',
        existing_liens: data.existing_liens || '',
        encumbrances: data.encumbrances || '',
        asking_value: data.asking_value || '',
        asking_value_currency: data.asking_value_currency || 'MXN',
        desired_structure: data.desired_structure || '',
        contribution_reason: data.contribution_reason || '',
        urgency: data.urgency || '',
        notes: data.notes || '',
        has_title_deed: data.has_title_deed || false,
        has_tax_clearance: data.has_tax_clearance || false,
        has_no_liens_cert: data.has_no_liens_cert || false,
        has_utility_bills: data.has_utility_bills || false,
      });
      setDocuments(data.documents || []);
    } catch (error) {
      toast.error('Error al cargar el inmueble');
      navigate('/app/properties');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        land_area_m2: formData.land_area_m2 ? parseFloat(formData.land_area_m2) : null,
        built_area_m2: formData.built_area_m2 ? parseFloat(formData.built_area_m2) : null,
        floors: formData.floors ? parseInt(formData.floors) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        current_rent_monthly: formData.current_rent_monthly ? parseFloat(formData.current_rent_monthly) : null,
        asking_value: formData.asking_value ? parseFloat(formData.asking_value) : null,
      };

      if (isEditing) {
        await api.put(`/properties/${id}`, payload);
        toast.success('Cambios guardados');
      } else {
        const response = await api.post('/properties', payload);
        const newId = response.data.property_id;
        navigate(`/app/properties/${newId}`, { replace: true });
        toast.success('Borrador creado');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const submitProperty = async () => {
    setSubmitting(true);
    try {
      // First save current changes
      const payload = {
        ...formData,
        land_area_m2: formData.land_area_m2 ? parseFloat(formData.land_area_m2) : null,
        built_area_m2: formData.built_area_m2 ? parseFloat(formData.built_area_m2) : null,
        floors: formData.floors ? parseInt(formData.floors) : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        current_rent_monthly: formData.current_rent_monthly ? parseFloat(formData.current_rent_monthly) : null,
        asking_value: formData.asking_value ? parseFloat(formData.asking_value) : null,
      };

      if (!isEditing) {
        const createRes = await api.post('/properties', payload);
        const newId = createRes.data.property_id;
        await api.post(`/properties/${newId}/submit`);
      } else {
        await api.put(`/properties/${id}`, payload);
        await api.post(`/properties/${id}/submit`);
      }
      
      toast.success('Inmueble enviado para revisión');
      navigate('/app/properties');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocumentUpload = async (file, category) => {
    if (!isEditing) {
      toast.error('Guarde el borrador primero para subir documentos');
      return;
    }
    
    setUploadingDoc(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', category);
      uploadData.append('related_entity_type', 'property');
      uploadData.append('related_entity_id', id);
      uploadData.append('is_confidential', 'true');
      uploadData.append('access_level', 'admin');
      
      await api.post('/documents/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Documento subido');
      fetchProperty();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir documento');
    } finally {
      setUploadingDoc(false);
    }
  };

  const stepProgress = ((currentStep + 1) / STEPS.length) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  // Check if property can be edited
  const canEdit = !property || ['draft', 'missing_info'].includes(property.status);

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          
          return (
            <div key={step.id} className="flex flex-col items-center flex-1">
              <div className={`
                h-10 w-10 rounded-full flex items-center justify-center border-2 transition-colors
                ${isCompleted ? 'bg-primary border-primary text-primary-foreground' : ''}
                ${isCurrent ? 'border-primary text-primary' : ''}
                ${!isCompleted && !isCurrent ? 'border-muted text-muted-foreground' : ''}
              `}>
                {isCompleted ? (
                  <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />
                ) : (
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                )}
              </div>
              <span className={`text-xs mt-2 text-center hidden md:block ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      <Progress value={stepProgress} className="h-1" />
    </div>
  );

  const renderBasicStep = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Título / Nombre del Inmueble *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Ej: Edificio de Oficinas Santa Fe"
          disabled={!canEdit}
          data-testid="property-title"
        />
      </div>

      <div className="space-y-2">
        <Label>Tipo de Propiedad *</Label>
        <Select
          value={formData.property_type}
          onValueChange={(v) => handleInputChange('property_type', v)}
          disabled={!canEdit}
        >
          <SelectTrigger data-testid="property-type">
            <SelectValue placeholder="Seleccione el tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="residential">Residencial</SelectItem>
            <SelectItem value="commercial">Comercial</SelectItem>
            <SelectItem value="office">Oficinas</SelectItem>
            <SelectItem value="industrial">Industrial</SelectItem>
            <SelectItem value="retail">Retail / Comercio</SelectItem>
            <SelectItem value="land">Terreno</SelectItem>
            <SelectItem value="mixed_use">Uso Mixto</SelectItem>
            <SelectItem value="hotel">Hotelería</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Tipo de Propietario *</Label>
        <Select
          value={formData.owner_type}
          onValueChange={(v) => handleInputChange('owner_type', v)}
          disabled={!canEdit}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Persona Física</SelectItem>
            <SelectItem value="company">Persona Moral / Empresa</SelectItem>
            <SelectItem value="representative">Representante / Intermediario</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderLocationStep = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="address">Dirección Completa *</Label>
        <Input
          id="address"
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          placeholder="Calle, número, colonia"
          disabled={!canEdit}
          data-testid="property-address"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">Ciudad *</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            placeholder="Ej: Ciudad de México"
            disabled={!canEdit}
            data-testid="property-city"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">Estado *</Label>
          <Input
            id="state"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            placeholder="Ej: CDMX"
            disabled={!canEdit}
            data-testid="property-state"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="country">País</Label>
          <Input
            id="country"
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            disabled={!canEdit}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="postal_code">Código Postal</Label>
          <Input
            id="postal_code"
            value={formData.postal_code}
            onChange={(e) => handleInputChange('postal_code', e.target.value)}
            placeholder="Ej: 06600"
            disabled={!canEdit}
          />
        </div>
      </div>
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="land_area">Superficie de Terreno (m²)</Label>
          <Input
            id="land_area"
            type="number"
            value={formData.land_area_m2}
            onChange={(e) => handleInputChange('land_area_m2', e.target.value)}
            placeholder="0"
            disabled={!canEdit}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="built_area">Superficie Construida (m²)</Label>
          <Input
            id="built_area"
            type="number"
            value={formData.built_area_m2}
            onChange={(e) => handleInputChange('built_area_m2', e.target.value)}
            placeholder="0"
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="floors">Número de Pisos</Label>
          <Input
            id="floors"
            type="number"
            value={formData.floors}
            onChange={(e) => handleInputChange('floors', e.target.value)}
            placeholder="0"
            disabled={!canEdit}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="year_built">Año de Construcción</Label>
          <Input
            id="year_built"
            type="number"
            value={formData.year_built}
            onChange={(e) => handleInputChange('year_built', e.target.value)}
            placeholder="Ej: 2015"
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Zonificación</Label>
          <Input
            value={formData.zoning}
            onChange={(e) => handleInputChange('zoning', e.target.value)}
            placeholder="Ej: Comercial H3"
            disabled={!canEdit}
          />
        </div>
        <div className="space-y-2">
          <Label>Uso de Suelo</Label>
          <Input
            value={formData.land_use}
            onChange={(e) => handleInputChange('land_use', e.target.value)}
            placeholder="Ej: Habitacional mixto"
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Estado de Ocupación</Label>
          <Select
            value={formData.occupancy_status}
            onValueChange={(v) => handleInputChange('occupancy_status', v)}
            disabled={!canEdit}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vacant">Vacío</SelectItem>
              <SelectItem value="owner_occupied">Ocupado por propietario</SelectItem>
              <SelectItem value="rented_single">Arrendado (un inquilino)</SelectItem>
              <SelectItem value="rented_multi">Arrendado (múltiples inquilinos)</SelectItem>
              <SelectItem value="partial">Parcialmente ocupado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Renta Mensual Actual (si aplica)</Label>
          <Input
            type="number"
            value={formData.current_rent_monthly}
            onChange={(e) => handleInputChange('current_rent_monthly', e.target.value)}
            placeholder="0"
            disabled={!canEdit}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Situación Legal / Escrituras</Label>
        <Select
          value={formData.legal_status}
          onValueChange={(v) => handleInputChange('legal_status', v)}
          disabled={!canEdit}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccione" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="clear">Libre de gravámenes, escriturada</SelectItem>
            <SelectItem value="mortgage">Con hipoteca activa</SelectItem>
            <SelectItem value="inheritance">En proceso de sucesión</SelectItem>
            <SelectItem value="litigation">En litigio</SelectItem>
            <SelectItem value="regularizing">En proceso de regularización</SelectItem>
            <SelectItem value="other">Otro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Gravámenes o Adeudos Existentes</Label>
        <Textarea
          value={formData.existing_liens}
          onChange={(e) => handleInputChange('existing_liens', e.target.value)}
          placeholder="Describa cualquier hipoteca, embargo, o adeudo fiscal..."
          rows={2}
          disabled={!canEdit}
        />
      </div>
    </div>
  );

  const renderFinancialStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="asking_value">Valor Solicitado / Expectativa *</Label>
          <Input
            id="asking_value"
            type="number"
            value={formData.asking_value}
            onChange={(e) => handleInputChange('asking_value', e.target.value)}
            placeholder="0"
            disabled={!canEdit}
            data-testid="property-value"
          />
        </div>
        <div className="space-y-2">
          <Label>Moneda</Label>
          <Select
            value={formData.asking_value_currency}
            onValueChange={(v) => handleInputChange('asking_value_currency', v)}
            disabled={!canEdit}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
              <SelectItem value="USD">USD - Dólar Americano</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Estructura Preferida</Label>
        <Select
          value={formData.desired_structure}
          onValueChange={(v) => handleInputChange('desired_structure', v)}
          disabled={!canEdit}
        >
          <SelectTrigger>
            <SelectValue placeholder="¿Cómo le gustaría participar?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sale">Venta total</SelectItem>
            <SelectItem value="joint_venture">Coinversión / Joint Venture</SelectItem>
            <SelectItem value="equity_partnership">Aportación a capital</SelectItem>
            <SelectItem value="sale_leaseback">Venta con arrendamiento</SelectItem>
            <SelectItem value="flexible">Flexible / Negociable</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Motivo de la Contribución</Label>
        <Textarea
          value={formData.contribution_reason}
          onChange={(e) => handleInputChange('contribution_reason', e.target.value)}
          placeholder="¿Por qué desea contribuir este inmueble? ¿Qué busca lograr?"
          rows={3}
          disabled={!canEdit}
        />
      </div>

      <div className="space-y-2">
        <Label>Urgencia</Label>
        <Select
          value={formData.urgency}
          onValueChange={(v) => handleInputChange('urgency', v)}
          disabled={!canEdit}
        >
          <SelectTrigger>
            <SelectValue placeholder="¿Qué tan pronto necesita cerrar?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="immediate">Inmediato (1-2 meses)</SelectItem>
            <SelectItem value="short">Corto plazo (3-6 meses)</SelectItem>
            <SelectItem value="medium">Mediano plazo (6-12 meses)</SelectItem>
            <SelectItem value="flexible">Sin urgencia / Flexible</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Notas Adicionales</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder="Cualquier información adicional relevante..."
          rows={3}
          disabled={!canEdit}
        />
      </div>
    </div>
  );

  const renderDocumentsStep = () => {
    const docCategories = [
      { category: 'property_title', label: 'Escritura / Título de Propiedad', field: 'has_title_deed' },
      { category: 'property_tax', label: 'Constancia de No Adeudo (Predial)', field: 'has_tax_clearance' },
      { category: 'legal', label: 'Certificado de Libertad de Gravamen', field: 'has_no_liens_cert' },
      { category: 'other', label: 'Recibos de Servicios', field: 'has_utility_bills' },
    ];

    const uploadedCategories = new Set(documents.map(d => d.category));

    return (
      <div className="space-y-6">
        <Alert>
          <Upload className="h-4 w-4" />
          <AlertDescription>
            Adjunte los documentos disponibles. No todos son requeridos inicialmente, pero aceleran el proceso de evaluación.
          </AlertDescription>
        </Alert>

        {!isEditing && (
          <Alert className="border-amber-500/20 bg-amber-500/5">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-amber-500">
              Guarde el borrador primero para poder subir documentos.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {docCategories.map((doc) => {
            const isUploaded = uploadedCategories.has(doc.category);
            const uploadedDoc = documents.find(d => d.category === doc.category);
            
            return (
              <div key={doc.category} className="p-4 border border-border rounded-sm space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={formData[doc.field]}
                      onCheckedChange={(checked) => handleInputChange(doc.field, checked)}
                      disabled={!canEdit}
                    />
                    <div>
                      <p className="font-medium">{doc.label}</p>
                      {isUploaded && uploadedDoc && (
                        <p className="text-xs text-muted-foreground">
                          Archivo: {uploadedDoc.original_filename}
                        </p>
                      )}
                    </div>
                  </div>
                  {isUploaded ? (
                    <Badge className="bg-emerald-500">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Subido
                    </Badge>
                  ) : (
                    <Badge variant="outline">Pendiente</Badge>
                  )}
                </div>
                
                {isEditing && canEdit && (
                  <div>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleDocumentUpload(e.target.files[0], doc.category);
                        }
                      }}
                      className="hidden"
                      id={`upload-${doc.category}`}
                      disabled={uploadingDoc}
                    />
                    <Label
                      htmlFor={`upload-${doc.category}`}
                      className="inline-flex items-center gap-2 px-4 py-2 border rounded-sm cursor-pointer hover:bg-muted transition-colors text-sm"
                    >
                      {uploadingDoc ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" strokeWidth={1.5} />
                      )}
                      {isUploaded ? 'Reemplazar' : 'Subir Archivo'}
                    </Label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderReviewStep = () => (
    <div className="space-y-6">
      {property?.status === 'missing_info' && property?.status_notes && (
        <Alert className="border-amber-500/20 bg-amber-500/5">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-500">
            <strong>Información solicitada por el revisor:</strong>
            <br />
            {property.status_notes}
          </AlertDescription>
        </Alert>
      )}

      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg">Resumen del Inmueble</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Título</p>
              <p className="font-medium">{formData.title || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Tipo</p>
              <p className="font-medium capitalize">{formData.property_type?.replace('_', ' ') || '-'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground">Ubicación</p>
              <p className="font-medium">{formData.address}, {formData.city}, {formData.state}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Superficie Terreno</p>
              <p className="font-medium">{formData.land_area_m2 ? `${formData.land_area_m2.toLocaleString()} m²` : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Superficie Construida</p>
              <p className="font-medium">{formData.built_area_m2 ? `${formData.built_area_m2.toLocaleString()} m²` : '-'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground">Valor Solicitado</p>
              <p className="font-medium text-primary text-xl">
                {formData.asking_value ? formatCurrency(parseFloat(formData.asking_value), formData.asking_value_currency) : '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Al enviar, su propuesta será revisada por nuestro equipo. Le contactaremos si necesitamos información adicional. El proceso de evaluación típicamente toma entre 2 y 4 semanas.
        </AlertDescription>
      </Alert>

      {canEdit && (
        <Button
          onClick={submitProperty}
          disabled={submitting || !formData.title || !formData.property_type || !formData.address || !formData.city || !formData.state}
          className="w-full btn-primary"
          data-testid="submit-property"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
          )}
          Enviar para Revisión
        </Button>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderBasicStep();
      case 1: return renderLocationStep();
      case 2: return renderDetailsStep();
      case 3: return renderFinancialStep();
      case 4: return renderDocumentsStep();
      case 5: return renderReviewStep();
      default: return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="property-form-page">
      <Link 
        to="/app/properties" 
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        Volver a mis inmuebles
      </Link>

      <div className="mb-8">
        <h1 className="heading-2 mb-2">
          {isEditing ? 'Editar Inmueble' : 'Proponer Nuevo Inmueble'}
        </h1>
        <p className="text-muted-foreground">
          {canEdit 
            ? 'Complete la información de su propiedad para enviarla a evaluación.'
            : 'Este inmueble ya fue enviado y está en proceso de revisión.'}
        </p>
      </div>

      {renderStepIndicator()}

      <Card className="card-dashboard">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{STEPS[currentStep].label}</CardTitle>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={saveDraft}
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" strokeWidth={1.5} />
              )}
              Guardar Borrador
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {renderCurrentStep()}

          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              Anterior
            </Button>
            
            {currentStep < 5 && (
              <Button
                onClick={() => setCurrentStep(prev => prev + 1)}
                className="btn-primary gap-2"
              >
                Siguiente
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertyFormPage;
